import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MoveItemRequest, MoveRequest, MoveTypes } from "../../../api/Inventory/Entities/InventoryMovementEntities";
import {
    inventoryCartDraftActions,
    leavesCartScreen,
    resolveCartDraftKey,
    resolveDraftAction,
    resolveUsableDraft,
    selectCartDraft
} from "../../../store/InventoryCartDraft";
import { accumulatesQuantity, matchesCartLine, resolveMergedQuantity } from "./inventoryCartMerge";
import { MAX_MOVE_ITEM_QUANTITY } from "../Shared/inventoryFormRules";
import useLoading, { useLoad } from "../../../components/Common/useLoading";
import InventoryOperationService from "../../../api/Inventory/InventoryOperationService";
import { useAuthorization } from "../../../components/Authorization/useAuthorization";
import { getPatientByMedicalRecord } from "../../../api/database";

export default function useInventoryCart(moveId, moveType, assignmentType, assignId = null, destination = null, movement = null, { persistDraft = false, history = null } = {}) {
    const [isAuthenticated, user, isAuthorized] = useAuthorization();
    const dispatch = useDispatch()
    const draftKey = resolveCartDraftKey({ moveType, assignmentType, moveId, assignId })
    const storedDraft = useSelector(state => selectCartDraft(state, draftKey))
    const restorableDraft = useRef(persistDraft ? resolveUsableDraft(storedDraft) : null)
    const isDraftHydrated = useRef(false)
    const isDraftRestored = useRef(false)
    const isDraftDiscarded = useRef(false)
    const [state, setState] = useState(new MoveRequest({
        moveType: moveType,
        assignment: {
            assignmentType: assignmentType,
            assignId: assignId
        },
        destination: destination,
    }))
    const [isLoading, withLoad] = useLoad()
    const isQuantityCappedMove = MoveTypes.IsEquivalent(moveType, MoveTypes.AddStock) || MoveTypes.IsEquivalent(moveType, MoveTypes.AdjustStock)

    useEffect(() => {
        if (user) {
            isDraftHydrated.current = true

            if (restorableDraft.current != null) {
                setState(restorableDraft.current)
                return;
            }

            if (movement != null && (movement.id > 0 || (movement.items?.length ?? 0) > 0)) {
                const move = new MoveRequest({
                    ...movement,
                    moveType: moveType,
                    assignment: {
                        assignmentType: assignmentType,
                        assignId: assignId
                    },
                    destination: destination,
                    userId: movement.userId == null ? user.sub : movement.userId,
                    items: movement.items.map(item => new MoveItemRequest({ ...item }))
                })
                setState({ ...move, confirmationUserId: user.sub })
                return;
            }
            // Sino lo cargamos desde el modulo de stock por el ID
            InitializeMovement(moveId, user.sub)
        }
    }, [isAuthenticated])

    useEffect(() => {
        const isPendingRestore = restorableDraft.current != null && !isDraftRestored.current
        const draftAction = resolveDraftAction({
            persistDraft,
            isHydrated: isDraftHydrated.current,
            isDiscarded: isDraftDiscarded.current,
            isPendingRestore,
            isRestoredStateApplied: state === restorableDraft.current,
            itemCount: state.items?.length ?? 0
        })

        if (draftAction === 'skip' || draftAction === 'wait') return;

        if (isPendingRestore) isDraftRestored.current = true

        dispatch(draftAction === 'save'
            ? inventoryCartDraftActions.saveDraft(draftKey, state)
            : inventoryCartDraftActions.clearDraft(draftKey))
    }, [persistDraft, draftKey, state, dispatch])

    const DiscardDraft = useCallback(() => {
        isDraftDiscarded.current = true
        dispatch(inventoryCartDraftActions.clearDraft(draftKey))
    }, [dispatch, draftKey])

    useEffect(() => {
        if (!persistDraft || history == null) return;

        const mountedPathname = history.location?.pathname ?? null

        return history.listen(nextLocation => {
            if (leavesCartScreen(mountedPathname, nextLocation)) DiscardDraft()
        })
    }, [persistDraft, history, DiscardDraft])

    async function InitializeMovement(moveId, userId) {
        await withLoad(async () => {

            if (userId == undefined || userId == "" || userId == " ") throw new Error("Invalid userId not found");

            const nextState = isNaN(moveId) || moveId == 0
                ? { ...state, userId: userId, confirmationUserId: userId }
                : await LoadMovement(userId, moveId)

            setState(nextState)
        })

    }
    const LoadMovement = async (userId, moveId) => {
        const move = ((await InventoryOperationService.GetPending(moveId)).Value);

        const patient = move.destination != null && move.destination.destinationId != null
            ? (await getPatientByMedicalRecord(move.destination.destinationId)).data
            : null

        return {
            ...move,
            confirmationUserId: userId,
            destination: {
                ...move.destination,
                destinationName: patient?.fullName ?? null
            }
        }
    }

    const AddItems = (cartItems) => {
        const itemRequest = cartItems.map(cartItem => new MoveItemRequest({
            id: 0,
            item: {
                externalId: cartItem.item.externalId,
                origin: cartItem.item.origin,
                type: cartItem.item.type,
                presentation: cartItem.item.presentation,
                inventoryItemId: cartItem.item.inventoryItemId,
                id: cartItem.item.inventoryItemId,
            },
            comment: cartItem.comment,
            release: cartItem.release,
            quantity: cartItem.quantity,
            assignment: cartItem.assignment
        }))

        setState({
            ...state,
            items: [...state.items, ...itemRequest]
        })
    }

    const AddItem = (item, release, quantity, assignment, availableQuantity, comment,
        originMoveItemId = null, partialQuantity = null, releaseTargetState = null, releaseCondition = null, dispensedKitQuantity = null) => {
        if (originMoveItemId != null) {
            const perUnitRequest = new MoveItemRequest({
                id: 0,
                item: {
                    externalId: item.externalId,
                    origin: item.origin,
                    type: item.type,
                    presentation: item.presentation,
                    inventoryItemId: item.inventoryItemId,
                    id: item.inventoryItemId,
                    drug: item.drug,
                },
                comment,
                release,
                quantity,
                assignment,
                originMoveItemId,
                partialQuantity,
                releaseTargetState,
                releaseCondition,
                dispensedKitQuantity
            });
            perUnitRequest.availableQuantity = availableQuantity;

            setState(prev => ({
                ...prev,
                items: [...prev.items, perUnitRequest]
            }));

            return perUnitRequest.uniqueId;
        }

        const current = state
            .items
            .find(i => matchesCartLine(i, { inventoryItemId: item.inventoryItemId, externalId: item.externalId, origin: item.origin, release, assignment }))

        if (current) {
            current.quantity = resolveMergedQuantity({
                existingQuantity: current.quantity,
                incomingQuantity: quantity,
                availableQuantity,
                accumulates: accumulatesQuantity(moveType),
                maxQuantity: isQuantityCappedMove ? MAX_MOVE_ITEM_QUANTITY : null,
            });

            current.comment = comment;
            setState({
                ...state,
                items: state.items
            })
            return
        }

        const itemRequest = new MoveItemRequest({
            id: 0,
            item: {
                externalId: item.externalId,
                origin: item.origin,
                type: item.type,
                presentation: item.presentation,
                inventoryItemId: item.inventoryItemId,
                id: item.inventoryItemId,
                drug: item.drug,
            },
            comment: comment,
            release,
            quantity,
            assignment
        })

        // se agrega para validar cuando se actualice las cantidades desde la tabla
        itemRequest.availableQuantity = availableQuantity;

        setState({
            ...state,
            items: [...state.items, itemRequest]
        })

        return itemRequest.uniqueId;
    }

    const UpdateQuantity = (item, quantity) => {
        const currentItem = state.items.find(i => i.uniqueId == item.uniqueId)
        if (currentItem && (currentItem.availableQuantity != null && currentItem.availableQuantity >= quantity) || currentItem.availableQuantity == null) {
            currentItem.quantity = quantity;
        }
        setState({
            ...state,
            items: state.items
        })
    }

    const UpdateItem = (item) => {
        const arr = Array.from(state.items)
        const index = state.items.findIndex(i => i.uniqueId == item.uniqueId)
        arr[index] = { ...arr[index], ...item }
        setState({
            ...state,
            items: arr
        })
    }

    const RemoveItem = (item) => {
        setState(prev => ({
            ...prev,
            items: prev.items.filter((i) => i.uniqueId != item.uniqueId)
        }));
    };

    const ClearItems = () => {
        setState({
            ...state,
            items: []
        })
    }

    const SetAssignId = (assignId) => {
        setState({
            ...state,
            assignment: {
                ...state.assignment,
                assignId: assignId?.toString()
            }
        })
    }
    const SetComment = (comment) => {
        setState({
            ...state,
            comment: comment
        })
    }

    const CartActions = {
        AddItem,
        AddItems,
        RemoveItem,
        ClearItems,
        UpdateQuantity,
        UpdateItem,
        SetAssignId,
        SetComment,
        DiscardDraft
    }


    return [state, isLoading, CartActions]
}